<template>
  <div class="content-dashboard">
    <div class="d-flex align-items-center justify-content-between">
      <div class="width-p">
        <h3 class="mb-2">{{ $t("reminder.title") }}</h3>
        <p v-html="$sanitize.sanitizeI18nContent($t('reminder.intro'))"></p>
      </div>
      <a href="#" class="btn btn-orange d-flex gap-2 align-items-center" @click.prevent="rappelStore.openCreateModal()">
        <img :src="plus" class="width-15" /> {{ $t("reminder.create_button") }}
      </a>
    </div>

    <div class="row">
      <div class="col-lg-6">
        <div class="search-block">
          <form @submit.prevent="handleSearch">
            <input
              type="text"
              class="form-control"
              id="search"
              v-model="searchForm.search"
              @input="handleSearchInput"
              placeholder="Rechercher un propriétaire (nom, email, téléphone...)"
            />
            <button class="search-btn" type="submit">
              <i class="ti ti-search"></i>
            </button>
            <button
              v-if="searchForm.search"
              type="button"
              class="clear-search-btn"
              @click="clearSearch"
              title="Effacer la recherche"
            >
              <i class="ti ti-x"></i>
            </button>
          </form>
        </div>
        <div v-if="searchForm.search" class="search-info mt-2">
          <small class="text-muted">
            <i class="ti ti-search me-1"></i>
            Recherche active : "{{ searchForm.search }}"
            <span v-if="filteredTotalReminders > 0"> - {{ filteredTotalReminders }} résultat(s) trouvé(s) </span>
            <span v-else class="text-warning"> - Aucun résultat trouvé </span>
          </small>
        </div>
      </div>
    </div>

    <div v-if="rappelStore.isLoading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status"></div>
      <p class="mt-3">Chargement des rappels...</p>
    </div>

    <div v-else-if="rappelStore.currentError" class="alert alert-danger mt-4">
      <h5>Erreur de chargement</h5>
      <p>{{ rappelStore.currentError }}</p>
      <button @click="loadReminders()" class="btn btn-outline-orange btn-sm">
        <i class="ti ti-refresh"></i> Réessayer
      </button>
    </div>

    <div
      v-else-if="!rappelStore.hasReminders || (searchForm.search && filteredOwnersWithReminders.length === 0)"
      class="row justify-content-center align-items-center mt-4 mt-5"
    >
      <div class="col-lg-4">
        <img :src="notif" />
        <p v-if="searchForm.search && rappelStore.hasReminders" class="blue-color mt-4 text-center">
          Aucun propriétaire trouvé pour "{{ searchForm.search }}"
        </p>
        <p v-else class="blue-color mt-4 text-center">{{ $t("reminder.empty") }}</p>
      </div>
    </div>

    <div v-else>
      <div class="d-flex justify-content-between align-items-center mt-4">
        <h3>Liste des rappels par propriétaire</h3>

        <a
          href="#"
          class="link orange-color"
          data-bs-toggle="modal"
          data-bs-target="#ProprietereAddModal"
          @click="openAddModal"
        >
          <img :src="addPersonSmall" />
          <span class="ps-2 text-decoration-underline">Ajouter un propriétaire</span>
        </a>
      </div>

      <div class="accordion accordion-block mt-3" id="rappelAccordion">
        <div v-for="(owner, index) in filteredOwnersWithReminders" :key="owner.proprietaire.id" class="accordion-item">
          <h4 class="accordion-header" :id="'heading' + index" @click="toggleSection(index)">
            <div class="d-flex justify-content-between w-100">
              <div>
                <span class="accordion-title">{{ getOwnerDisplayName(owner.proprietaire) }}</span>
                <a href="#" class="link-icon ps-2" @click.stop="editOwner(owner.proprietaire)">
                  <img :src="editIcon" />
                </a>
              </div>

              <div class="d-flex gap-2 align-items-center">
                <button class="btn btn-outline-orange" @click.stop="deleteOwnerReminders(owner.proprietaire)">
                  <span>
                    <img :src="deleteIcon" />
                  </span>
                  Supprimer le propriétaire
                </button>

                <i :class="['ti', activeIndex === index ? 'ti-angle-up' : 'ti-angle-down', 'transition']"></i>
              </div>
            </div>
          </h4>

          <div class="accordion-collapse collapse" :class="{ show: activeIndex === index }">
            <div class="accordion-body">
              <div v-if="owner.reminders && owner.reminders.length > 0">
                <RappelAccordion
                  v-for="reminder in owner.reminders"
                  :key="reminder.id"
                  :reminder="reminder"
                  @edit="handleReminderEdit"
                  @delete="handleReminderDelete"
                  class="mb-3"
                />
              </div>
              <div v-else class="alert alert-info">Aucun rappel configuré pour ce propriétaire.</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <ReminderCreateModal
      v-if="rappelStore.showCreateModal"
      @close="rappelStore.closeModals()"
      @submit="handleCreateReminder"
    />

    <ReminderEditModal
      v-if="rappelStore.showEditModal"
      :reminder="rappelStore.editingReminder"
      @close="rappelStore.closeModals()"
      @submit="handleUpdateReminder"
    />

    <div class="modal fade" id="ProprietereAddModal" ref="ProprietereAddModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-width">
        <div class="modal-content">
          <div class="modal-header justify-content-end">
            <button type="button" class="close-link" data-bs-dismiss="modal">
              <i class="ti ti-close"></i>
            </button>
          </div>
          <div class="modal-body padd-modal">
            <div class="title-modal">
              <img :src="addPersonLarge" />
              <h3>Ajouter un propriétaire</h3>
            </div>

            <form class="mt-4">
              <div class="row justify-content-center align-items-center">
                <div class="col-lg-9">
                  <div class="d-flex gap-3 mb-4">
                    <div class="input-form">
                      <label class="label-control"> Nom*</label>
                      <input
                        type="text"
                        class="form-control"
                        v-model="proprietaireForm.lastName"
                        :class="{ 'is-invalid': formErrors.lastName }"
                        required
                      />
                      <div v-if="formErrors.lastName" class="invalid-feedback">
                        {{ formErrors.lastName }}
                      </div>
                    </div>
                    <div class="input-form">
                      <label class="label-control">Prénom*</label>
                      <input
                        type="text"
                        class="form-control"
                        v-model="proprietaireForm.firstName"
                        :class="{ 'is-invalid': formErrors.firstName }"
                        required
                      />
                      <div v-if="formErrors.firstName" class="invalid-feedback">
                        {{ formErrors.firstName }}
                      </div>
                    </div>
                  </div>

                  <div class="d-flex gap-3">
                    <div class="input-form">
                      <label class="label-control"> E-mail*</label>
                      <input
                        type="email"
                        class="form-control"
                        v-model="proprietaireForm.email"
                        :class="{ 'is-invalid': formErrors.email }"
                        required
                      />
                      <div v-if="formErrors.email" class="invalid-feedback">
                        {{ formErrors.email }}
                      </div>
                    </div>
                    <div class="input-form">
                      <label class="label-control">Téléphone portable</label>
                      <div class="input-group">
                        <select
                          class="form-select telephone-select"
                          v-model="proprietaireForm.indicatifCellphone"
                        >
                          <option
                            v-for="country in countryDialCodes"
                            :key="country.code"
                            :value="country.dialCode"
                          >
                            {{ country.displayCode }} {{ country.code }}
                          </option>
                        </select>
                        <input
                          type="tel"
                          class="form-control telephone-input"
                          v-model="proprietaireForm.cellphone"
                          v-only-digits
                          :class="{ 'is-invalid': formErrors.cellphone }"
                        />
                      </div>
                      <div v-if="formErrors.cellphone" class="invalid-feedback">
                        {{ formErrors.cellphone }}
                      </div>
                    </div>
                  </div>

                  <div class="d-flex justify-content-center gap-3 mt-3">
                    <button
                      type="button"
                      class="btn btn-outline-orange w-25"
                      data-bs-dismiss="modal"
                      @click="closeModal"
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      class="btn btn-orange w-25"
                      @click="submitProprietaire"
                      :disabled="proprietairesStore.loading"
                    >
                      <span v-if="proprietairesStore.loading"> <i class="ti ti-loader"></i> Ajout... </span>
                      <span v-else>Valider</span>
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import notif from "@/assets/images/notif.svg";
  import plus from "@/assets/images/plus.svg";
  import deleteIcon from "@/assets/icons/delete.svg";
  import editIcon from "@/assets/icons/edit.svg";
  import addPersonSmall from "@/assets/icons/add-person-small.svg";
  import addPersonLarge from "@/assets/icons/add-person-large.svg";
  import RappelAccordion from "@/components/RappelAccordion.vue";
  import ReminderCreateModal from "@/components/ReminderCreateModal.vue";
  import ReminderEditModal from "@/components/ReminderEditModal.vue";
  import { useRappelStore } from "@/stores/rappelStore";
  import { useProprietairesStore } from "@/stores/esupplyvet/proprietairesStore";
  import { useProductsStore } from "@/stores/productsStore";
  import { COUNTRIES } from "@/config/constants";
  import { getCountryCallingCode } from "libphonenumber-js";

  export default {
    name: "RappelView",
    components: {
      RappelAccordion,
      ReminderCreateModal,
      ReminderEditModal,
    },
    data() {
      return {
        notif,
        plus,
        deleteIcon,
        editIcon,
        addPersonSmall,
        addPersonLarge,
        activeIndex: null,
        searchForm: {
          search: "",
        },
        proprietaireForm: {
          firstName: "",
          lastName: "",
          email: "",
          cellphone: "",
          indicatifCellphone: "33",
        },
        formErrors: {},
        searchTimeout: null,
      };
    },
    computed: {
      rappelStore() {
        return useRappelStore();
      },
      proprietairesStore() {
        return useProprietairesStore();
      },
      productsStore() {
        return useProductsStore();
      },
      countryDialCodes() {
        return COUNTRIES.map((country) => {
          try {
            const dialCode = getCountryCallingCode(country.code);
            return {
              ...country,
              dialCode: dialCode,
              displayCode: `+${dialCode}`,
              displayName: `${country.name} (+${dialCode})`,
            };
          } catch (error) {
            return null;
          }
        })
        .filter((c) => c)
        .sort((a, b) => a.name.localeCompare(b.name));
      },
      filteredOwnersWithReminders() {
        if (!this.searchForm.search.trim()) {
          return this.rappelStore.ownersWithReminders;
        }

        const searchTerm = this.searchForm.search.toLowerCase().trim();

        return this.rappelStore.ownersWithReminders.filter(owner => {
          const proprietaire = owner.proprietaire;
          if (!proprietaire) return false;

          const nom = (proprietaire.nom || "").toLowerCase();
          if (nom.includes(searchTerm)) return true;

          const email = (proprietaire.email || "").toLowerCase();
          if (email.includes(searchTerm)) return true;

          const telephone = (proprietaire.telephone || "").toLowerCase();
          if (telephone.includes(searchTerm)) return true;

          return false;
        });
      },
      filteredTotalReminders() {
        return this.filteredOwnersWithReminders.reduce((total, owner) => {
          return total + (owner.reminders ? owner.reminders.length : 0);
        }, 0);
      },
    },
    async mounted() {
      await this.loadReminders();

      await Promise.all([this.proprietairesStore.fetchProprietaires(), this.preloadProducts()]);
    },
    methods: {
      async loadReminders() {
        try {
          await this.rappelStore.fetchGroupedReminders();
        } catch (error) {
          console.error("Erreur lors du chargement des rappels:", error);
        }
      },

      async preloadProducts() {
        try {
          await this.productsStore.fetchAllReminderProducts();
        } catch (error) {
          console.error("Erreur lors du préchargement des produits:", error);
        }
      },

      handleSearch() {},

      handleSearchInput() {},

      clearSearch() {
        this.searchForm.search = "";
      },

      toggleSection(index) {
        this.activeIndex = this.activeIndex === index ? null : index;
      },

      getOwnerDisplayName(owner) {
        if (!owner) return "Propriétaire inconnu";

        if (owner.nom && owner.nom.trim()) {
          return owner.nom.trim();
        }

        return owner.email || "Propriétaire inconnu";
      },

      async handleCreateReminder(reminderData) {
        try {
          const success = await this.rappelStore.createReminder(reminderData);
          if (success) {
            console.log("Rappel créé avec succès");
          }
        } catch (error) {
          console.error("Erreur lors de la création du rappel:", error);
        }
      },

      async handleReminderEdit(reminder) {
        try {
          const enrichedReminder = { ...reminder };

          const ownerData = this.rappelStore.ownersWithReminders.find(owner =>
            owner.reminders.some(r => r.id === reminder.id)
          );

          if (ownerData && ownerData.proprietaire) {
            enrichedReminder.ownerId = ownerData.proprietaire.id;
            enrichedReminder.proprietaire = ownerData.proprietaire;
          } else {
            console.warn("Impossible de trouver le propriétaire pour le rappel:", reminder.id);
          }

          await this.rappelStore.prepareEdit(enrichedReminder);
        } catch (error) {
          console.error("Erreur lors de la préparation de l'édition:", error);
        }
      },

      async handleUpdateReminder(updateData) {
        try {
          if (this.rappelStore.editingReminder) {
            const success = await this.rappelStore.updateReminder(this.rappelStore.editingReminder.id, updateData);
            if (success) {
              console.log("Rappel mis à jour avec succès");
            }
          }
        } catch (error) {
          console.error("Erreur lors de la mise à jour du rappel:", error);
        }
      },

      async handleReminderDelete(reminder) {
        const confirmMessage = `Êtes-vous sûr de vouloir supprimer ce rappel ?\n\nProduit : ${reminder.nomSac}\nRation journalière : ${reminder.rationJour}`;

        if (!confirm(confirmMessage)) {
          return;
        }

        try {
          const success = await this.rappelStore.deleteReminder(reminder.id);
          if (success) {
            console.log("Rappel supprimé avec succès");
          }
        } catch (error) {
          console.error("Erreur lors de la suppression du rappel:", error);
        }
      },

      async deleteOwnerReminders(owner) {
        const ownerName = this.getOwnerDisplayName(owner);
        const confirmMessage = `Êtes-vous sûr de vouloir supprimer TOUS les rappels de ${ownerName} ?\n\nCette action est irréversible.`;

        if (!confirm(confirmMessage)) {
          return;
        }

        try {
          const ownerData = this.rappelStore.ownersWithReminders.find(o => o.proprietaire.id === owner.id);

          if (ownerData && ownerData.reminders) {
            const deletePromises = ownerData.reminders.map(reminder => this.rappelStore.deleteReminder(reminder.id));

            await Promise.all(deletePromises);
          }
        } catch (error) {
          console.error("Erreur lors de la suppression des rappels:", error);
          alert("Une erreur est survenue lors de la suppression des rappels.");
        }
      },

      openAddModal() {
        this.resetProprietaireForm();
        this.proprietairesStore.resetState();
      },

      resetProprietaireForm() {
        this.proprietaireForm = {
          firstName: "",
          lastName: "",
          email: "",
          cellphone: "",
          indicatifCellphone: "33",
        };
        this.formErrors = {};
      },

      validateProprietaireForm() {
        this.formErrors = {};
        let isValid = true;

        if (!this.proprietaireForm.lastName.trim()) {
          this.formErrors.lastName = "Le nom est obligatoire";
          isValid = false;
        }

        if (!this.proprietaireForm.firstName.trim()) {
          this.formErrors.firstName = "Le prénom est obligatoire";
          isValid = false;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!this.proprietaireForm.email.trim()) {
          this.formErrors.email = "L'email est obligatoire";
          isValid = false;
        } else if (!emailRegex.test(this.proprietaireForm.email)) {
          this.formErrors.email = "Format d'email invalide";
          isValid = false;
        }

        if (this.proprietaireForm.cellphone.trim()) {
          const phoneRegex = /^[0-9+\-\s()]+$/;
          if (!phoneRegex.test(this.proprietaireForm.cellphone)) {
            this.formErrors.cellphone = "Format de téléphone invalide";
            isValid = false;
          }
        }

        return isValid;
      },

      async submitProprietaire() {
        if (!this.validateProprietaireForm()) {
          return;
        }

        try {
          await this.proprietairesStore.addProprietaire(this.proprietaireForm);
          this.closeModal();

          await this.loadReminders();
        } catch (error) {
          if (error.response?.status === 400) {
            this.formErrors.email = "Cet email existe déjà";
          } else {
            console.error("Erreur lors de l'ajout du propriétaire:", error);
          }
        }
      },

      closeModal() {
        const modal = this.$refs.ProprietereAddModal;
        if (modal) {
          modal.querySelector('[data-bs-dismiss="modal"]')?.click();
        }
        this.resetProprietaireForm();
      },

      editOwner(owner) {
        alert(`Édition du propriétaire ${this.getOwnerDisplayName(owner)} - Fonctionnalité à implémenter`);
      },
    },
  };
</script>

<style scoped>
  @import "@/assets/css/components/CalculRation.css";

  .search-block {
    position: relative;
  }

  .search-block form {
    position: relative;
    display: flex;
    align-items: center;
  }

  .search-block .form-control {
    padding-right: 80px;
  }

  .search-btn {
    margin-top: 16px;
    position: absolute;
    right: 40px;
    top: 1px !important;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: #6c757d;
    font-size: 16px;
    cursor: pointer;
    padding: 5px;
    z-index: 2;
  }

  .search-btn:hover {
    color: #ec621a;
  }

  .clear-search-btn {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: #6c757d;
    font-size: 14px;
    cursor: pointer;
    padding: 5px;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
  }

  .clear-search-btn:hover {
    background-color: #f8f9fa;
    color: #dc3545;
  }

  .search-info {
    font-style: italic;
  }

  .search-info i {
    color: #ec621a;
  }

  .search-btn i {
    transition: transform 0.2s ease;
  }

  .search-btn:hover i {
    transform: scale(1.1);
  }

  .search-block .form-control::placeholder {
    color: #adb5bd;
    font-style: italic;
  }

  .search-block .form-control:focus::placeholder {
    color: #6c757d;
  }

    .input-group {
    display: flex;
    width: 100%;
    border: none !important;
  }

  .telephone-select {
    background-color: #e9ecef;
    border-right: none;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-color: #ced4da;
    max-width: 140px;
  }

  .telephone-select:focus {
    z-index: 2;
    border-color: #86b7fe;
    box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
  }

  .telephone-input {
    border-left: none;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-color: #ced4da;
  }

  .telephone-input:focus {
    z-index: 2;
    border-color: #86b7fe;
    box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
  }

  /* Ensure the border between them looks seamless or handled correctly */
  .telephone-select:not(:focus) + .telephone-input:not(:focus) {
    border-left: 1px solid #ced4da;
  }

  .form-select {
    color: #212529 !important;
    border: 1px solid #36373987 !important;
    border-radius: 35px 0 0 35px !important;
  }
</style>
